import React, { useContext } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormGenerator, ModalWrapper } from '@microservices/wiskey-react-components'
import { Box, Typography } from '@mui/material'

import { getLabelScriptConfig } from '@pages/MapCreateOrEdit/components/AddPointGroupDialog/helpers'
import { PageContext } from '@pages/MapCreateOrEdit/MapCreateOrEdit'

import { CommandParameters } from '@components/CommandParameters'
import { JavaScriptField } from '@components/JavaScriptField'
import { ModalFormContainer } from '@components/ModalFormContainer'
import { ColorSettings } from '@components/TextSettings/components/ColorSettings'

import { FIELD_BIND_TYPE_ENUM, TYPES_SOURCE_OPTIONS } from '@constants'

import { useAddPointGroupDialog } from './hooks/useAddPointGroupDialog'

export const AddPointGroupDialog = () => {
  const { t } = useTranslation()

  const { addPointGroupDialogData } = useContext(PageContext)

  const {
    state: {
      methods,
      isDirty,
      watchLabelValue,
      watchLabelBindType,
      isShowPointColorValueScriptDialog,
      isShowLabelValueScriptDialog,
      watchGetDataCommandName,
      watchObjectCode,
      watchPointColorBindType,
      watchPointColorValue,
    },
    handlers: {
      handleSave,
      handleClose,
      handleSavePointColorValueScript,
      handleSaveLabelValueScript,
      handleChangePointColor,
      handleClosePointColorValueScriptDialog,
      handleOpenLabelValueScriptDialog,
      handleOpenPointColorValueScriptDialog,
      handleCloseLabelValueScriptDialog,
    },
    inputs: { labelInputs, generalInputs, labelBindTypeInput, getCommandInputs, pointColorInputs },
    data: { selectedObjectFields },
  } = useAddPointGroupDialog()

  return (
    <FormProvider {...methods}>
      <ModalWrapper
        isShow
        disabledSave={!isDirty}
        title={t(`addPointGroupDialog.${addPointGroupDialogData?.modalType}`)}
        width={1000}
        containerStyle={{
          px: 0.5,
          py: 0.5,
        }}
        onClose={handleClose}
        onSave={handleSave}
      >
        <ModalFormContainer maxHeight={600}>
          <FormGenerator inputs={generalInputs} mainGridSpacing={0} />
          <Typography mb={1} variant='h6'>
            {t('addPointGroupDialog.pointStyle')}
          </Typography>
          <Box component={'fieldset'} mb={2} position='relative'>
            <legend>{t('addPointGroupDialog.color')}</legend>
            <FormGenerator inputs={pointColorInputs} mainGridSpacing={0} />
            {watchPointColorBindType?.id === FIELD_BIND_TYPE_ENUM.JS && (
              <JavaScriptField
                isShowScriptDialog={isShowPointColorValueScriptDialog}
                scriptValue={watchPointColorValue}
                onCloseScriptDialog={handleClosePointColorValueScriptDialog}
                onOpenScriptDialog={handleOpenPointColorValueScriptDialog}
                onSaveScript={handleSavePointColorValueScript}
                {...getLabelScriptConfig(selectedObjectFields)}
              />
            )}
            {watchPointColorBindType?.id === FIELD_BIND_TYPE_ENUM.STATIC && (
              <ColorSettings
                clearableColor
                isEdit
                color={watchPointColorValue}
                label={t('addPointGroupDialog.value')}
                labelMaxWidth={120}
                labelPlacement={'left'}
                onChangeColor={handleChangePointColor}
              />
            )}
          </Box>
          {Boolean(watchObjectCode?.id) && (
            <Box sx={{ mt: 2 }}>
              <Typography mb={1} variant='h6'>
                {t('addPointGroupDialog.command')}
              </Typography>
              <FormGenerator inputs={getCommandInputs} />
              {Boolean(watchGetDataCommandName?.id) && (
                <>
                  <CommandParameters
                    disableFieldType
                    commandCode={watchGetDataCommandName?.id as string}
                    containerSx={{ mb: 1 }}
                    label={t('contextMenuCreate.addMenuPointModal.parameters.label')}
                    labelWidth={120}
                    name='getDataCommand.parameters'
                    parametersMode='params'
                    sourceCode={watchObjectCode?.id as string}
                    sourceTitle={watchObjectCode?.label as string}
                    typeSource={TYPES_SOURCE_OPTIONS.OBJECT}
                  />

                  <CommandParameters
                    disableFieldType
                    commandCode={watchGetDataCommandName?.id as string}
                    containerSx={{ mb: 1 }}
                    label={t('contextMenuCreate.addMenuPointModal.body.label')}
                    labelWidth={120}
                    name='getDataCommand.body'
                    parametersMode='body'
                    sourceCode={watchObjectCode?.id as string}
                    sourceTitle={watchObjectCode?.label as string}
                    typeSource={TYPES_SOURCE_OPTIONS.OBJECT}
                  />
                </>
              )}
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant='h6'>{t('addPointGroupDialog.label')}</Typography>
            <FormGenerator inputs={labelBindTypeInput} mainGridSpacing={0} />
            {watchLabelBindType?.id === FIELD_BIND_TYPE_ENUM.JS && (
              <JavaScriptField
                isShowScriptDialog={isShowLabelValueScriptDialog}
                scriptValue={watchLabelValue}
                onCloseScriptDialog={handleCloseLabelValueScriptDialog}
                onOpenScriptDialog={handleOpenLabelValueScriptDialog}
                onSaveScript={handleSaveLabelValueScript}
                {...getLabelScriptConfig(selectedObjectFields)}
              />
            )}
            <FormGenerator inputs={labelInputs} mainGridSpacing={0} />
          </Box>
        </ModalFormContainer>
      </ModalWrapper>
    </FormProvider>
  )
}
